import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { compose } from 'redux';
import i18n from 'i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';

import PATH from 'routes/paths';
import TextField from '../../stories/ui/TextField/TextField';
import Button from '../../stories/ui/Button/Button';
import Tooltip from '../../stories/ui/Tooltip/Tooltip';
import withRef from '../../hocs/withRef';
import RedQuestionIcon from '../../stories/ui/Icons/RedQuestionIcon';
import withHelmet from '../../hocs/withHelmet';
import Alert from '../../alerts/Alert';
import { authRequest } from '../../api/requestWrappers/AuthRequest';
import { buildPasswordSchema } from '../../utils/passwordValidation';
import { LoginLogo } from './Login';

const ErrorIconWithRef = withRef(RedQuestionIcon);

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.gray[100]};
`;

const FormContainer = styled.div`
  width: 340px;
  min-height: 320px;
  background-color: ${({ theme }) => theme.palette.themeCommon.white.main};
  border-radius: 0.5rem;
  padding: 0 1.5rem 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 72px;
  font-size: 1rem;

  &:after {
    content: '';
    position: absolute;
    width: calc(100% + 3rem);
    bottom: 0;
    left: -1.5rem;
    right: -1.5rem;
    border-bottom: 1px solid
      ${({ theme }) => theme.palette.themeCommon.black.light};
  }
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.gray[700]};
  margin: 1.5rem 0;
  line-height: 1.5;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

const StyledSubmitButton = styled(Button)`
  font-size: 1rem;
  width: 100%;
`;

const Message = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.gray[1900]};
  margin: 1.5rem 0;
  line-height: 1.5;
`;

const BackLink = styled(Link)`
  display: block;
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const useTextFieldErrorClasses = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.themeCommon.red.light,
  },
}));

function ResetPassword() {
  const { t } = useTranslation();
  const textFieldErrorClasses = useTextFieldErrorClasses();
  const history = useHistory();
  const location = useLocation();
  const [linkExpired, setLinkExpired] = useState(false);

  const token = useMemo(
    () => new URLSearchParams(location.search).get('token') || '',
    [location.search]
  );

  const Schema = Yup.object().shape({
    password: buildPasswordSchema(t),
    repeatPassword: Yup.string()
      .when('password', {
        is: (val) => val && val.length > 0,
        then: Yup.string().oneOf(
          [Yup.ref('password')],
          'Passwords must be the same'
        ),
      })
      .required(t('passwordRequired')),
  });

  // No token in the URL — the link is malformed or stripped.
  if (!token) {
    return (
      <PageContainer>
        <LoginLogo />
        <FormContainer>
          <Header>
            <Trans i18nKey="resetPassword.title" />
          </Header>
          <Message>
            <Trans i18nKey="resetPassword.invalidLink" />
          </Message>
          <BackLink to={PATH.AUTH.FORGOT_PASSWORD}>
            <Trans i18nKey="resetPassword.requestNewLink" />
          </BackLink>
        </FormContainer>
      </PageContainer>
    );
  }

  // BE rejected with AUTH_ACCOUNT_LINK_INVALID — show the expired message.
  if (linkExpired) {
    return (
      <PageContainer>
        <LoginLogo />
        <FormContainer>
          <Header>
            <Trans i18nKey="resetPassword.title" />
          </Header>
          <Message>
            <Trans i18nKey="resetPassword.expired" />
          </Message>
          <BackLink to={PATH.AUTH.FORGOT_PASSWORD}>
            <Trans i18nKey="resetPassword.requestNewLink" />
          </BackLink>
        </FormContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <LoginLogo />
      <FormContainer>
        <Header>
          <Trans i18nKey="resetPassword.title" />
        </Header>
        <Subtitle>
          <Trans i18nKey="resetPassword.subtitle" />
        </Subtitle>
        <Formik
          initialValues={{ password: '', repeatPassword: '' }}
          validationSchema={Schema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await authRequest.resetPassword(token, values.password);
              history.push(`${PATH.AUTH.LOGIN}?reset=success`);
            } catch (e) {
              if (e?.response?.data?.code === 'AUTH_ACCOUNT_LINK_INVALID') {
                setLinkExpired(true);
              } else {
                Alert.showError('resetPassword.errorGeneric');
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <StyledForm>
              <Field name="password">
                {({ field, meta }) => (
                  <StyledTextField
                    type="password"
                    id="reset-password-password"
                    placeholder={t('newPassword')}
                    {...field}
                    InputProps={
                      meta.touched && meta.error
                        ? {
                            endAdornment: (
                              <Tooltip title={meta.error} color="danger" open>
                                <ErrorIconWithRef />
                              </Tooltip>
                            ),
                            classes: textFieldErrorClasses,
                          }
                        : undefined
                    }
                  />
                )}
              </Field>
              <Field name="repeatPassword">
                {({ field, meta }) => (
                  <StyledTextField
                    type="password"
                    id="reset-password-repeat"
                    placeholder={t('repeatNewPassword')}
                    {...field}
                    InputProps={
                      meta.touched && meta.error
                        ? {
                            endAdornment: (
                              <Tooltip title={meta.error} color="danger" open>
                                <ErrorIconWithRef />
                              </Tooltip>
                            ),
                            classes: textFieldErrorClasses,
                          }
                        : undefined
                    }
                  />
                )}
              </Field>
              <StyledSubmitButton
                color="primary"
                size="large"
                type="submit"
                isLoading={isSubmitting}
              >
                <Trans i18nKey="resetPassword.submit" />
              </StyledSubmitButton>
              <BackLink to={PATH.AUTH.LOGIN}>
                <Trans i18nKey="forgotPassword.backToLogin" />
              </BackLink>
            </StyledForm>
          )}
        </Formik>
      </FormContainer>
    </PageContainer>
  );
}

const wrappers = compose(withHelmet(i18n.t('metaTitles.resetPassword')));

export default wrappers(ResetPassword);
